import type {ReactElement} from 'react';
import {useContext} from 'react';
import {EventStyle} from '@Components/event-wizard/event-style';
import {EventWizardDateTime} from '@Components/event-wizard/event-wizard-date-time';
import {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue';
import {NameInput} from '@Components/event-wizard/name-input';
import type {EventWizardDetailsProps} from '@Components/event-wizard/event-wizard-details/event-wizard-details.types';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {useEventWizardIsDesignPrefillContentLoading} from '@Components/event-wizard/event-wizard.hooks';
import {TicketingAnnouncementBanner} from '@Components/event-wizard/ticketing-announcement-banner/ticketing-announcement-banner';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {Text, TextSize} from '@Components/text';
import {isOrganiserEligibleForPaidTicketing} from '@Libraries/event-ticketing.library';

export function EventWizardDetails({isForReviewPage = false}: EventWizardDetailsProps): ReactElement {
  const isPrefillContentLoading = useEventWizardIsDesignPrefillContentLoading();

  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);
  const spacingBottom = isSmallScreen ? 'spacing-m-b-2' : 'spacing-m-b-4';

  /** Heading and its sub-line together — the small-screen wizard supplies its own header, so neither shows there. */
  const getHeading = (): ReactElement | null => {
    if (isForReviewPage || isSmallScreen) {
      return null;
    }
    return (
      <div className="spacing-m-b-4">
        <h4 className="heading-h4 spacing-p-0 content-body">{window.i18next.t('pmwjs_create_your_event')}</h4>
        <Text className="ct-neutral-secondary" val={window.i18next.t('pmwjs_tell_us_a_bit_about_your_event')} size={TextSize.MD} />
      </div>
    );
  };

  /**
   * Shown to organisers who could sell paid tickets today, which is the audience the banner's copy is written
   * for. The review page summarises an event that is already configured, so an announcement has nothing to add.
   */
  const getTicketingAnnouncement = (): ReactElement | null => {
    if (isForReviewPage || !isOrganiserEligibleForPaidTicketing()) {
      return null;
    }
    return (
      <div className={`${spacingBottom} _full-width`}>
        <TicketingAnnouncementBanner />
      </div>
    );
  };

  const getBodyContent = (): ReactElement => {
    return (
      <>
        <div className={`${spacingBottom} _full-width`}>
          <NameInput />
        </div>

        <div className={`${spacingBottom} _full-width`}>
          <EventWizardDateTime />
        </div>

        <div className={`${spacingBottom} _full-width`}>
          <EventWizardVenue />
        </div>

        <div className={`${spacingBottom} _full-width`}>
          <EventStyle />
        </div>
      </>
    );
  };

  const getLoadingView = (): ReactElement => {
    return (
      <div className="flex-column-justify-center">
        <SkeletonBrick className="spacing-m-b-6" width={100} height={24} />
        <SkeletonBrick className="spacing-m-b-8 _full-width" height={40} />
        <SkeletonBrick className="spacing-m-b-6" width={100} height={24} />
        <SkeletonBrick className="spacing-m-b-8 _full-width" height={40} />
        <SkeletonBrick className="spacing-m-b-6" width={100} height={24} />
        <SkeletonBrick className="spacing-m-b-8 _full-width" height={40} />
      </div>
    );
  };

  return (
    <>
      {getHeading()}
      {getTicketingAnnouncement()}
      {isPrefillContentLoading ? getLoadingView() : getBodyContent()}
    </>
  );
}
